import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { CompetitorsService } from './competitorsApi';
import { IPaginationPayload } from '../../../../interfaces/shared/IPaginationPayload';
import { AxiosError } from 'axios';

const initialState: any = {
    competitorsAreLoading: false,
    competitorIsLoading: false,
    allCompetitorsAreLoading: false,
    competitorsModalAreLoading: false,
    requestStatus: 'default',
    message: '',
};

export const tryToFetchCompetitors = createAsyncThunk<any, IPaginationPayload>(
    'competitors/tryToFetchCompetitors',
    async ({ pageNumber, pageSize, filters, data }) => {
        const result = await CompetitorsService.tryToFetchCompetitors(
            pageNumber,
            pageSize,
            data,
            filters,
        );
        return result?.data;
    },
);

export const tryToFetchCompetitorsModal = createAsyncThunk<any, IPaginationPayload>(
    'competitors/tryToFetchCompetitorsModal',
    async ({ pageNumber, pageSize, filters, data }) => {
        const result = await CompetitorsService.tryToFetchCompetitors(
            pageNumber,
            pageSize,
            data,
            filters,
        );
        return result?.data;
    },
);

export const tryToFetchAllCompetitors = createAsyncThunk<any, { type?: string } | undefined>(
    'competitors/tryToFetchAllCompetitors',
    async (data) => {
        const result = await CompetitorsService.tryToFetchAllCompetitors(data);
        return result?.data;
    }
);

export const tryToGenerateNewCompetitors = createAsyncThunk<any, void>(
    'competitors/tryToGenerateNewCompetitors',
    async () => {
        const result = await CompetitorsService.tryToGenerateNewCompetitors();
        return result?.data;
    }
);

export const tryToAcceptSuggestedCompetitors = createAsyncThunk<any, any>(
    'competitors/tryToAcceptSuggestedCompetitors',
    async (data, { rejectWithValue }) => {
        try {
            const result = await CompetitorsService.tryToAcceptSuggestedCompetitors(data);
            return result?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    }
);

export const tryToEditCompetitor = createAsyncThunk<any, any>(
    'competitors/tryToEditCompetitor',
    async (data, { rejectWithValue }) => {
        try {
            const result = await CompetitorsService.tryToEditCompetitor(data);
            return result?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    }
);

export const tryToAddCompetitors = createAsyncThunk<any, any>(
    'competitors/tryToAddCompetitors',
    async (data, { rejectWithValue }) => {
        try {
            const result = await CompetitorsService.tryToAddCompetitors(data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToDeleteCompetitor = createAsyncThunk<any, any>(
    'competitors/tryToDeleteCompetitor',
    async (data, { rejectWithValue }) => {
        try {
            const result = await CompetitorsService.tryToDeleteCompetitor(data);
            return result?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    }
);

export const competitorsSlice = createSlice({
    name: 'competitors',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // tryToFetchCompetitors
            .addCase(tryToFetchCompetitors.pending, (state) => {
                state.competitorsAreLoading = true;
            })
            .addCase(tryToFetchCompetitors.rejected, (state) => {
                state.competitorsAreLoading = false;
            })
            .addCase(tryToFetchCompetitors.fulfilled, (state, action) => {
                state.competitorsAreLoading = false;
                state.competitors = action.payload;
            })

            // tryToFetchCompetitorsModal
            .addCase(tryToFetchCompetitorsModal.pending, (state) => {
                state.competitorsModalAreLoading = true;
            })
            .addCase(tryToFetchCompetitorsModal.rejected, (state) => {
                state.competitorsModalAreLoading = false;
            })
            .addCase(tryToFetchCompetitorsModal.fulfilled, (state, action) => {
                state.competitorsModalAreLoading = false;
                state.modalCompetitors = action.payload;
            })

            // tryToFetchAllCompetitors
            .addCase(tryToFetchAllCompetitors.pending, (state) => {
                state.allCompetitorsModalAreLoading = true;
            })
            .addCase(tryToFetchAllCompetitors.rejected, (state) => {
                state.allCompetitorsAreLoading = false;
            })
            .addCase(tryToFetchAllCompetitors.fulfilled, (state, action) => {
                state.allCompetitorsAreLoading = false;
                state.allCompetitors = action.payload;
            })

            // tryToDeleteCompetitor
            .addCase(tryToDeleteCompetitor.pending, (state) => {
                state.competitorIsLoading = true;
            })
            .addCase(tryToDeleteCompetitor.rejected, (state) => {
                state.competitorIsLoading = false;
            })
            .addCase(tryToDeleteCompetitor.fulfilled, (state, action) => {
                state.competitorIsLoading = false;
                if (state.competitors?.data.elements)
                    state.competitors.data.elements =
                        { ...state }.competitors?.data.elements.filter((prompt: any) => prompt?._id !== action?.meta?.arg?.id) || [];
                if (state.modalCompetitors?.data.elements)
                    state.modalCompetitors.data.elements =
                        { ...state }.modalCompetitors?.data.elements.filter((prompt: any) => prompt?._id !== action?.meta?.arg?.id) || [];
            })

            // tryToEditCompetitor
            .addCase(tryToEditCompetitor.pending, (state) => {
                state.competitorIsLoading = true;
            })
            .addCase(tryToEditCompetitor.rejected, (state) => {
                state.competitorIsLoading = false;
            })
            .addCase(tryToEditCompetitor.fulfilled, (state, action) => {
                state.competitorIsLoading = false;
                if (state.competitors?.data.elements)
                    state.competitors.data.elements =
                        { ...state }.competitors?.data.elements.map((competitor: any) => {
                            if (competitor?._id === action?.meta?.arg?.id) {
                                return {
                                    ...competitor || {},
                                    brand: action?.meta?.arg?.brand,
                                    active: action?.meta?.arg?.active,
                                    type: action?.meta?.arg?.type,
                                    websiteUrl: action?.meta?.arg?.websiteUrl
                                }
                            } else {
                                return competitor
                            }
                        }) || [];
            })

            // tryToAddCompetitors
            .addCase(tryToAddCompetitors.pending, (state) => {
                state.competitorIsLoading = true;
            })
            .addCase(tryToAddCompetitors.rejected, (state) => {
                state.competitorIsLoading = false;
            })
            .addCase(tryToAddCompetitors.fulfilled, (state, action) => {
                state.competitorIsLoading = false;
                if (state.modalCompetitors?.data.elements)
                    state.modalCompetitors.data.elements = [...state.modalCompetitors?.data.elements || [], ...action?.payload?.data?.competitors || []]
                if (state.competitors?.data.elements)
                    state.competitors.data.elements = [...state.competitors?.data.elements || [], ...action?.payload?.data?.competitors || []]
            })

    },
});

export default competitorsSlice.reducer;